import { DashboardConfigurationDependencies } from '../../../../dashboard-config/dashboard-config.model';
import { DashboardDefinition } from '../../../_shared/dashboard-definition.model';
import {
  AddExportSidePropertyDashboardDataTransformer,
  DashboardDataTransformer,
  DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer,
  DistrictIdNameMappingDashboardDataTransformer,
  EmployedStatesSumAppenderDashboardDataTransformer,
  EmployedSubMarketsSumAppenderDashboardDataTransformer,
  FilterPassedStatesAndDistrictsBySideDashboardDataTransformer,
  NameAscDashboardDataTransformer,
  Q2ToYearPeriodIdDashboardDataTransformer,
  SortByYearDataTransformer,
  StateIdNameMappingDashboardDataTransformer,
  SubMarketIdNameMappingDashboardDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import {
  LocalRowColumnArrayFilterValueFilterApplicator,
  LocalRowPassesFilterApplicatorResultToColumnApplicator,
} from '../../../../filters/dashboard_filter_applicators';
import {
  DashboardAutomaticPeriodSingleSelectFilter,
  DashboardDistrictsMultiSelectFilter,
  DashboardExportSideSingleSelectFilter,
  DashboardMethodologyFromActivatedRouteSingleSelectFilter,
  DashboardPeriodTypeSingleSelectFilter,
  DashboardRemoteSubMarketSingleSelectFilter,
  DashboardStatesMultiSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';

// ——— identity ———

/**
 * Library shell id for A/B employed-districts comparison (4x5x4 / NVDuel).
 * Live UI is still the legacy map component; this id closes the Bibliothek loop.
 */
export const COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID = 'comparison-duel' as const;

/** Sole product dashboard currently backed by the duel shell. */
export const COMPARISON_EMPLOYED_DISTRICTS_DASHBOARD_ID = '4x5x4' as const;

// ——— filter names ———

/** A/B state multiselect filter names (map template + pass columns). */
export const COMPARISON_EMPLOYED_DISTRICTS_STATES_A = 'states_multiselect_a' as const;
export const COMPARISON_EMPLOYED_DISTRICTS_STATES_B = 'states_multiselect_b' as const;

/** A/B district multiselect filter names (map template + pass columns). */
export const COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_A = 'districts_multiselect_a' as const;
export const COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_B = 'districts_multiselect_b' as const;

export const COMPARISON_EMPLOYED_DISTRICTS_STATES_MULTISELECT_NAMES = {
  A: COMPARISON_EMPLOYED_DISTRICTS_STATES_A,
  B: COMPARISON_EMPLOYED_DISTRICTS_STATES_B,
} as const;

export const COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_MULTISELECT_NAMES = {
  A: COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_A,
  B: COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_B,
} as const;

// ——— builders (live UI + library refresh) ———

/** Shared filters for live 4x5x4 map + library duel live-refresh. */
export function buildComparisonDuelFilters(
  deps: DashboardConfigurationDependencies,
): DashboardFilter<any>[] {
  const { store, localDatasetsService, activatedRoute, methodologiesLimiterService, datasetName } =
    deps;

  return [
    new DashboardPeriodTypeSingleSelectFilter({
      name: 'period',
      showLabel: false,
      showFullOptionLabel: true,
      excludedOptions: ['month'],
      renderAtChildFilterComponent: true,
    }),
    new DashboardAutomaticPeriodSingleSelectFilter(store, datasetName, {
      parentFilterName: 'period',
    }),
    new DashboardRemoteSubMarketSingleSelectFilter(localDatasetsService, {}),
    new DashboardMethodologyFromActivatedRouteSingleSelectFilter(
      localDatasetsService,
      store,
      activatedRoute,
      methodologiesLimiterService,
      {},
    ),
    new DashboardStatesMultiSelectFilter(localDatasetsService, {
      name: COMPARISON_EMPLOYED_DISTRICTS_STATES_A,
      label: 'Bundesland',
      addSumGermany: false,
      defaultValues: ['3', '4'],
      allInitiallySelected: false,
      showInFilterComponent: false,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_states_multiselect_a',
        new LocalRowColumnArrayFilterValueFilterApplicator('state_id'),
      ),
    }),
    new DashboardStatesMultiSelectFilter(localDatasetsService, {
      name: COMPARISON_EMPLOYED_DISTRICTS_STATES_B,
      label: 'Bundesland',
      addSumGermany: false,
      defaultValues: ['2'],
      allInitiallySelected: false,
      showInFilterComponent: false,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_states_multiselect_b',
        new LocalRowColumnArrayFilterValueFilterApplicator('state_id'),
      ),
    }),
    new DashboardDistrictsMultiSelectFilter(localDatasetsService, {
      name: COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_A,
      label: 'Landkreis',
      parentFilterName: COMPARISON_EMPLOYED_DISTRICTS_STATES_A,
      defaultValues: ['331', '335'],
      allInitiallySelected: false,
      showInFilterComponent: false,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_districts_multiselect_a',
        new LocalRowColumnArrayFilterValueFilterApplicator('district_id'),
      ),
    }),
    new DashboardDistrictsMultiSelectFilter(localDatasetsService, {
      name: COMPARISON_EMPLOYED_DISTRICTS_DISTRICTS_B,
      label: 'Landkreis',
      parentFilterName: COMPARISON_EMPLOYED_DISTRICTS_STATES_B,
      defaultValues: ['230', '245'],
      allInitiallySelected: false,
      showInFilterComponent: false,
      applicator: new LocalRowPassesFilterApplicatorResultToColumnApplicator(
        'pass_districts_multiselect_b',
        new LocalRowColumnArrayFilterValueFilterApplicator('district_id'),
      ),
    }),
    new DashboardExportSideSingleSelectFilter({
      showInFilterComponent: false,
    }),
  ];
}

export function buildComparisonDuelPreFilterTransformers(
  deps: DashboardConfigurationDependencies,
): DashboardDataTransformer[] {
  return [
    new UndefinedStateToSumGermanyDashboardDataTransformer(),
    new DefinedPeriodIdentifiersToPeriodLabelDashboardDataTransformer(
      deps.store,
      deps.datasetName,
      { enabledPeriods: ['year', 'quarter'] },
    ),
  ];
}

export function buildComparisonDuelPostFilterTransformers(
  deps: DashboardConfigurationDependencies,
): DashboardDataTransformer[] {
  return [
    new Q2ToYearPeriodIdDashboardDataTransformer(),
    new StateIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
    new DistrictIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
    new SubMarketIdNameMappingDashboardDataTransformer(deps.localDatasetsService),
    new NameAscDashboardDataTransformer('state_name'),
    new SortByYearDataTransformer(),
  ];
}

/** Live dashboard export pipeline only (not used by library refresh). */
export function buildComparisonDuelExportTransformers(): DashboardDataTransformer[] {
  return [
    new AddExportSidePropertyDashboardDataTransformer(),
    new FilterPassedStatesAndDistrictsBySideDashboardDataTransformer({
      statesFilterValueKeyPrefix: 'pass_states_multiselect',
      districtsFilterValueKeyPrefix: 'pass_districts_multiselect',
    }),
    new EmployedStatesSumAppenderDashboardDataTransformer({
      totalValueKey: 'employed_total_sub_market_district',
      marginalvalueKey: 'employed_marginal_sub_market_district',
      socialSecurityValueKey: 'employed_social_security_sub_market_district',
    }),
    new EmployedSubMarketsSumAppenderDashboardDataTransformer({
      totalValueKey: 'employed_total_sub_market_district',
      marginalvalueKey: 'employed_marginal_sub_market_district',
      socialSecurityValueKey: 'employed_social_security_sub_market_district',
    }),
  ];
}

// ——— library definition ———

/**
 * Headless library wiring for comparison duel (dashboard `4x5x4`).
 * Live UI (`Db4x5x4ComparisonEmployedDistrictsComponent`) reuses the builders above.
 * Not a live-route DI definition — library refresh until a full definition migration.
 */
export type ComparisonDuelLibraryDefinition = Pick<
  DashboardDefinition<Record<string, never>>,
  | 'shellId'
  | 'dashboardId'
  | 'filters'
  | 'preFilterTransformers'
  | 'postFilterTransformers'
  | 'chartTransformers'
  | 'view'
>;

export const COMPARISON_EMPLOYED_DISTRICTS_LIBRARY_DEFINITION: ComparisonDuelLibraryDefinition = {
  dashboardId: COMPARISON_EMPLOYED_DISTRICTS_DASHBOARD_ID,
  shellId: COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID,
  view: {},
  filters: buildComparisonDuelFilters,
  preFilterTransformers: buildComparisonDuelPreFilterTransformers,
  postFilterTransformers: buildComparisonDuelPostFilterTransformers,
  chartTransformers: () => [],
};

/** Library lookup for the A/B employed-districts duel shell (dashboard `4x5x4`). */
export function getComparisonDuelDefinition(
  dashboardId: string,
): ComparisonDuelLibraryDefinition | undefined {
  if (dashboardId !== COMPARISON_EMPLOYED_DISTRICTS_DASHBOARD_ID) {
    return undefined;
  }
  return COMPARISON_EMPLOYED_DISTRICTS_LIBRARY_DEFINITION;
}
